import type { FC } from 'react';
import moment from 'moment-timezone';
import { EDR_COLORS, edrMutedStyle } from './edrTokens';

const EDR_TIME_12H = 'hh:mm:ss A';
const EDR_DATE = 'DD MMM YYYY';

/**
 * Backend timestamps are UTC (e.g. `2026-08-21T08:51:57Z`).
 * Same UTC → browser-local approach as `absoluteTime` in `src/utils/time.ts`.
 */
const toLocalMoment = (value: string | number | null | undefined) => {
  if (value == null) return null;

  const raw =
    typeof value === 'number' ? value : String(value).trim().replace(' ', 'T');
  if (raw === '' || raw === '—') return null;

  const m = moment.utc(raw);
  return m.isValid() ? m.local() : null;
};

/** Inline `hh:mm:ss A · DD MMM YYYY` in the viewer's local timezone (12-hour). */
export const formatEdrDateTime = (value?: string | number | null): string => {
  const m = toLocalMoment(value);
  if (!m) {
    const fallback = value == null ? '' : String(value).trim();
    return fallback || '—';
  }
  return `${m.format(EDR_TIME_12H)} · ${m.format(EDR_DATE)}`;
};

/** Two-line timestamp for table/detail cells (browser local time, 12-hour). */
export const EdrDateTime: FC<{ value?: string | number | null }> = ({ value }) => {
  const m = toLocalMoment(value);
  if (!m) {
    const raw = value == null ? '' : String(value).trim();
    if (!raw || raw === '—') return <span style={edrMutedStyle}>—</span>;
    return <span style={{ fontSize: 12, color: EDR_COLORS.textSecondary }}>{raw}</span>;
  }

  return (
    <span style={{ display: 'inline-block', lineHeight: 1.25, textAlign: 'right' }}>
      <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: EDR_COLORS.textPrimary }}>
        {m.format(EDR_TIME_12H)}
      </span>
      <span style={{ display: 'block', fontSize: 11, color: EDR_COLORS.textMuted }}>
        {m.format(EDR_DATE)}
      </span>
    </span>
  );
};
